<template>
  <p-container>
    <p-row>
      <p-col grow>
        <p-row align-center>
          <p-col xs12 sm6 lg2>
            <p-text-field
              v-model="filter.searchCriteria"
              context-icon="search"
              :label="$t('core.app.search')"
              clearable
            ></p-text-field>
          </p-col>
          <p-col xs12 sm6 lg2>
            <p-select
              v-model="filter.levels"
              context-icon="build"
              clearable
              :label="$t('core.page.appLog.action.level')"
              :items="levelsFromAppLogs"
            ></p-select>
          </p-col>
          <p-col xs12 sm6 lg4>
            <p-date-time-field
              v-model="filter.dateFrom"
              :label="$t('core.page.appLog.action.from')"
            ></p-date-time-field>
          </p-col>
          <p-col xs12 sm6 lg4>
            <p-date-time-field
              v-model="filter.dateTo"
              :label="$t('core.page.appLog.action.to')"
            ></p-date-time-field>
          </p-col>
        </p-row>
      </p-col>
    </p-row>
    <p-list-view ref="ordersList" v-model="list" :pagination.sync="pagination" :service-function="populateData" :columns="columns">
      <template #table-actions>
        <p-button @click="refreshList">
          <p-icon>refresh</p-icon>
        </p-button>
      </template>
      <template #entry-actions="{entry}">
        <p-row dense nowrap>
          <p-button @click="showFilterToTimeSpanDialog(entry.timeStamp.value,timeSpanTime)">
            <p-icon>compress</p-icon>
          </p-button>
          <p-button @click="showAppLogDetail(entry)">
            <p-icon>find_in_page</p-icon>
          </p-button>
        </p-row>
      </template>
    </p-list-view>
    <p-dialog v-if="isSetTimePeriodDialogVisible" :title="$t('core.page.appLog.label.filterTimeSpan')" :has-close-button="false">
      <template #content>
        <p-col>
          <p-time-field v-model="timeSpanTime" class="ix-5" :label="$t('core.page.appLog.label.filterTimeSpanTextfield')"></p-time-field>
        </p-col>
      </template>
      <template #actions>
        <p-button secondary @click="isSetTimePeriodDialogVisible=false">
          {{ $t('core.app.cancel') }}
        </p-button>
        <p-button @click="filterToTimeSpan(timeSpanBaseTime, timeSpanTime)">
          <p-icon>compress</p-icon> {{ $t('core.page.appLog.label.filterTimeSpanButton') }}
        </p-button>
      </template>
    </p-dialog>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import ResponsePagingInfo from '../../../plugins/servicelayer/responseTypes/responsePagingInfo';
  import GetAppLogsRequestViewModel from '../../../services/v1/viewModel/request/AppLogs/GetAppLogsRequestViewModel';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      timeSpanTime: '00:00:15:000',
      isSetTimePeriodDialogVisible: false as boolean,
      timeSpanBaseTime: '' as string,
      pagination: {} as ResponsePagingInfo,
      filter: {
        searchCriteria: undefined as string | undefined,
        levels: undefined as any,
        dateFrom: undefined as string | undefined,
        dateTo: undefined as string | undefined,
      },
      innerPagination: {
        'paging.CurrentPage': undefined as number | undefined,
        'paging.PageSize': undefined as number | undefined,
        'paging.IncludeCount': undefined as boolean | undefined,
        'paging.NoPaging': undefined as boolean | undefined,
      },
      list: {},
      columns: [] as {}[],
    }),
    computed: {
      request() {
        return new GetAppLogsRequestViewModel().fromModel({ ...this.filter, ...this.innerPagination });
      },
      levelsFromAppLogs(): {caption: string, id?: string, detail?: string}[] {
        return [
          {
            caption: this.$t('core.page.appLog.select.debug'),
            id: 'debug',
          },
          {
            caption: this.$t('core.page.appLog.select.information'),
            id: 'information',
          },
          {
            caption: this.$t('core.page.appLog.select.warning'),
            id: 'warning',
          },
          {
            caption: this.$t('core.page.appLog.select.error'),
            id: 'error',
          },
          {
            caption: this.$t('core.page.appLog.select.fatal'),
            id: 'fatal',
          },
        ];
      },
    },
    watch: {
      filter: {
        deep: true,
        handler() {
          this.pagination.currentPage = 1;
          this.innerPagination['paging.CurrentPage'] = 1;
          this.$debounce(() => {
            this.refreshList();
          }, 300, this)();
        },
      },
      request() {
        this.writeRequestToQuery();
      },
    },
    beforeMount() {
      this.getQueryFromRequest();
      this.pagination.currentPage = this.innerPagination['paging.CurrentPage'] !;
      this.pagination.pageSize = this.innerPagination['paging.PageSize'] !;
    },
    mounted() {
      this.columns = [
        { value: '', field: 'filter' },
        { value: this.$t('core.page.appLog.list.id'), textAlign: 'left', field: 'id' },
        { value: this.$t('core.page.appLog.list.level'), textAlign: 'left', field: 'level' },
        {
          value: this.$t('core.page.appLog.list.timestamp'), textAlign: 'left', field: 'timeStamp', format: (value : any) => this.$date(value).format('DD.MM.YYYY HH:mm:ss:SSS'),
        },
        {
          value: this.$t('core.page.appLog.list.message'), textAlign: 'left', field: 'message', format: (value : any) => this.$format.ellipsis(value, 100),
        },
        {
          value: this.$t('core.page.appLog.list.exception'), textAlign: 'left', field: 'exception', format: (value : any) => this.$format.ellipsis(value, 140),
        },
      ];
    },
    methods: {
      getQueryFromRequest() {
        const queryParams = new GetAppLogsRequestViewModel();
        queryParams.fromQuery(this.$routerUtils.getQueryParams());
        this.filter.searchCriteria = queryParams.searchCriteria;
        this.filter.levels = queryParams.levels;
        this.filter.dateFrom = queryParams.dateFrom;
        this.filter.dateTo = queryParams.dateTo;

        this.innerPagination['paging.CurrentPage'] = queryParams['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = queryParams['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = queryParams['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = queryParams['paging.NoPaging'];
      },
      async writeRequestToQuery() {
        this.$debounce(() => {
          this.$routerUtils.updateQueryParams(this.request.toQuery(), { preserve: false });
        }, 200, this.writeRequestToQuery)();
      },
      filterToTimeSpan(origin: string, margin: string) {
        const originDate = this.$date(origin);
        const marginSplit = margin?.split(':') ?? [];
        const marginHours = Number.parseFloat(marginSplit[0] ?? '0');
        const marginMinutes = Number.parseFloat(marginSplit[1] ?? '0');
        let marginSeconds = Number.parseFloat(marginSplit[2] ?? '0');
        let marginMilliseconds = Number.parseFloat(marginSplit[3] ?? '0');
        if (marginSplit[2]?.includes('.')) {
          const secondsSplit = marginSplit[2].split('.');
          marginSeconds = Number.parseFloat(secondsSplit[0] ?? '0');
          marginMilliseconds = Number.parseFloat(secondsSplit[1] ?? '0');
        }

        const start = originDate
          .subtract(marginHours, 'hour')
          .subtract(marginMinutes, 'minute')
          .subtract(marginSeconds, 'second')
          .subtract(marginMilliseconds, 'millisecond');

        const end = originDate
          .add(marginHours, 'hour')
          .add(marginMinutes, 'minute')
          .add(marginSeconds, 'second')
          .add(marginMilliseconds, 'millisecond');

        this.filter.dateFrom = start.format();
        this.filter.dateTo = end.format();
        this.isSetTimePeriodDialogVisible = false;
      },
      refreshList() {
        const ordersList = this.$refs.ordersList as any;
        ordersList?.populateList();
      },
      formatEntryText(entry: any) {
        const date = this.$date(entry.timeStamp.value).format('DD.MM.YYYY HH:mm:ss:SSS');
        const id = `${this.$t('core.page.appLog.list.id')}: ${entry.id.value}\n\n`;
        const level = `${this.$t('core.page.appLog.list.level')}: ${entry.level.value}\n\n`;
        const timeStamp = `${this.$t('core.page.appLog.list.timestamp')}: ${date}\n\n`;
        const messages = `${this.$t('core.page.appLog.list.message')}: ${entry.message.value}\n\n`;
        const exception = `${this.$t('core.page.appLog.list.exception')}:\n${entry.exception.value}`;

        const information = id + level + timeStamp + messages + exception;

        return information;
      },
      showAppLogDetail(entry: any) {
        this.$dialog.notify('AppLog', this.formatEntryText(entry));
      },
      showFilterToTimeSpanDialog(timeSpanBaseTime: string, timeSpanTime:string) {
        this.isSetTimePeriodDialogVisible = true;
        this.timeSpanBaseTime = timeSpanBaseTime;
        this.timeSpanTime = timeSpanTime;
      },
      populateData(params: any) {
        this.innerPagination['paging.CurrentPage'] = params.query['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = params.query['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = params.query['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = params.query['paging.NoPaging'];
        return this.$service.api.core.appLogs.getAppLogs(this.request);
      },
    },
  });
</script>
